package com.kanati.admin.features.auth

import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import com.kanati.admin.components.ActionButton
import com.kanati.admin.components.BannerTone
import com.kanati.admin.components.SectionCard
import com.kanati.admin.components.StatusBanner
import com.kanati.admin.components.TextField
import com.kanati.admin.state.MaterialThemePalette
import com.kanati.admin.state.ThemeMode
import kotlinx.coroutines.delay
import org.jetbrains.compose.web.attributes.InputType
import org.jetbrains.compose.web.attributes.placeholder
import org.jetbrains.compose.web.css.DisplayStyle
import org.jetbrains.compose.web.css.FlexDirection
import org.jetbrains.compose.web.css.display
import org.jetbrains.compose.web.css.flexDirection
import org.jetbrains.compose.web.css.fontFamily
import org.jetbrains.compose.web.css.gap
import org.jetbrains.compose.web.css.padding
import org.jetbrains.compose.web.css.px
import org.jetbrains.compose.web.dom.Button
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.H1
import org.jetbrains.compose.web.dom.Img
import org.jetbrains.compose.web.dom.Input
import org.jetbrains.compose.web.dom.P
import org.jetbrains.compose.web.dom.Text

@Composable
fun LoginScreen(
    email: String,
    password: String,
    error: String?,
    progressMessage: String?,
    isSubmitting: Boolean,
    isRestoringSession: Boolean,
    theme: MaterialThemePalette,
    themeMode: ThemeMode,
    onEmailChange: (String) -> Unit,
    onPasswordChange: (String) -> Unit,
    onLogin: () -> Unit,
    onToggleTheme: () -> Unit,
) {
    var showPassword by remember { mutableStateOf(false) }
    var loadingSeconds by remember { mutableStateOf(0) }

    LaunchedEffect(isSubmitting, isRestoringSession) {
        loadingSeconds = 0
        if (isSubmitting || isRestoringSession) {
            while (true) {
                delay(1_000)
                loadingSeconds += 1
            }
        }
    }

    val loadingMessage = when {
        isRestoringSession -> "Restoring your admin session... ${loadingSeconds}s elapsed."
        isSubmitting -> "${progressMessage ?: "Signing in and loading your admin access..."} ${loadingSeconds}s elapsed."
        else -> null
    }

    Div(
        attrs = {
            style {
                fontFamily("\"SF Pro Text\", \"SF Pro Display\", \"Avenir Next\", \"Segoe UI\", sans-serif")
                padding(24.px)
                property("min-height", "100vh")
                property(
                    "background",
                    "linear-gradient(180deg, ${theme.background} 0%, ${theme.surfaceContainerLow} 55%, ${theme.surfaceContainerHigh} 100%)"
                )
                property("color", theme.onBackground)
                property("display", "flex")
                property("align-items", "center")
                property("justify-content", "center")
            }
        }
    ) {
        Div(
            attrs = {
                style {
                    display(DisplayStyle.Flex)
                    flexDirection(FlexDirection.Column)
                    gap(18.px)
                    property("width", "min(100%, 520px)")
                }
            }
        ) {
            Div(
                attrs = {
                    style {
                        property("display", "flex")
                        property("justify-content", "space-between")
                        property("align-items", "center")
                        property("gap", "12px")
                        property("flex-wrap", "wrap")
                    }
                }
            ) {
                Div(
                    attrs = {
                        style {
                            display(DisplayStyle.Flex)
                            flexDirection(FlexDirection.Column)
                            gap(8.px)
                        }
                    }
                ) {
                    P(
                        attrs = {
                            style {
                                property("margin", "0")
                                property("color", theme.onSurfaceVariant)
                                property("font-size", "0.78rem")
                                property("font-weight", "700")
                                property("letter-spacing", "0.12em")
                                property("text-transform", "uppercase")
                            }
                        }
                    ) {
                        Text("Operator Access")
                    }

                    H1(
                        attrs = {
                            style {
                                property("margin", "0")
                                property("font-family", "\"SF Pro Display\", \"Avenir Next\", sans-serif")
                                property("font-size", "2rem")
                                property("line-height", "1")
                                property("letter-spacing", "-0.05em")
                            }
                        }
                    ) {
                        Text("Kanati Admin")
                    }

                    P(
                        attrs = {
                            style {
                                property("margin", "0")
                                property("color", theme.onSurfaceVariant)
                                property("font-size", "0.96rem")
                                property("line-height", "1.5")
                            }
                        }
                    ) {
                        Text("Sign in with your operator account to load your access scope and continue.")
                    }
                }

                ActionButton(
                    label = if (themeMode == ThemeMode.Light) "Dark Mode" else "Light Mode",
                    onClick = onToggleTheme,
                    theme = theme,
                    isPrimary = false,
                )
            }

            SectionCard(
                title = "Operator Login",
                theme = theme,
                description = "Use the email and password configured for Kanati Admin access.",
            ) {
                if (isRestoringSession) {
                    StatusBanner(
                        message = loadingMessage ?: "Restoring your admin session...",
                        theme = theme,
                    )
                }

                if (isSubmitting) {
                    StatusBanner(
                        message = loadingMessage ?: "Signing in and loading your admin access...",
                        theme = theme,
                    )
                }

                error?.let {
                    StatusBanner(
                        message = it,
                        theme = theme,
                        tone = BannerTone.Error,
                    )
                }

                TextField(
                    theme = theme,
                    label = "Email",
                    value = email,
                    placeholder = "john-doe@test.com",
                    onValueChange = onEmailChange,
                )

                PasswordField(
                    theme = theme,
                    value = password,
                    showPassword = showPassword,
                    onValueChange = onPasswordChange,
                    onToggleVisibility = { showPassword = !showPassword },
                )

                ActionButton(
                    label = when {
                        isRestoringSession -> "Restoring..."
                        isSubmitting -> "Signing In..."
                        else -> "Sign In"
                    },
                    onClick = onLogin,
                    theme = theme,
                    isDisabled = isSubmitting || isRestoringSession,
                )
            }
        }
    }
}

@Composable
private fun PasswordField(
    theme: MaterialThemePalette,
    value: String,
    showPassword: Boolean,
    onValueChange: (String) -> Unit,
    onToggleVisibility: () -> Unit,
) {
    Div(
        attrs = {
            style {
                display(DisplayStyle.Flex)
                flexDirection(FlexDirection.Column)
                gap(8.px)
            }
        }
    ) {
        P(
            attrs = {
                style {
                    property("margin", "0")
                    property("color", theme.onSurfaceVariant)
                    property("font-size", "0.8rem")
                    property("font-weight", "700")
                    property("letter-spacing", "0.08em")
                    property("text-transform", "uppercase")
                }
            }
        ) {
            Text("Password")
        }

        Div(
            attrs = {
                style {
                    property("position", "relative")
                    property("width", "100%")
                }
            }
        ) {
            Input(type = if (showPassword) InputType.Text else InputType.Password) {
                value(value)
                placeholder("Password")
                onInput { onValueChange(it.value) }
                style {
                    padding(14.px, 52.px, 14.px, 16.px)
                    property("box-sizing", "border-box")
                    property("width", "100%")
                    property("border-radius", "18px")
                    property(
                        "background",
                        "linear-gradient(180deg, ${theme.surfaceContainerHighest} 0%, ${theme.surfaceVariant} 100%)"
                    )
                    property("color", theme.onSurface)
                    property("border", "1px solid ${theme.outline}")
                    property("box-shadow", "inset 0 1px 0 ${theme.scrim}, 0 12px 24px ${theme.shadow}")
                    property("font-size", "0.98rem")
                    property("font-weight", "550")
                    property("letter-spacing", "-0.01em")
                }
            }

            Button(
                attrs = {
                    onClick { onToggleVisibility() }
                    attr("aria-label", if (showPassword) "Hide password" else "Show password")
                    attr("title", if (showPassword) "Hide password" else "Show password")
                    style {
                        padding(0.px)
                        property("position", "absolute")
                        property("top", "50%")
                        property("right", "14px")
                        property("transform", "translateY(-50%)")
                        property("width", "28px")
                        property("height", "28px")
                        property("display", "inline-flex")
                        property("align-items", "center")
                        property("justify-content", "center")
                        property("background", "transparent")
                        property("border", "none")
                        property("cursor", "pointer")
                        property("touch-action", "manipulation")
                        property("-webkit-tap-highlight-color", "transparent")
                    }
                }
            ) {
                Img(
                    src = if (showPassword) eyeOffIcon(theme.primary) else eyeIcon(theme.primary),
                    attrs = {
                        attr("alt", "")
                        attr("aria-hidden", "true")
                        style {
                            property("display", "block")
                            property("width", "18px")
                            property("height", "18px")
                        }
                    }
                )
            }
        }
    }
}

private fun eyeIcon(strokeColor: String): String =
    svgIconDataUri(
        svgBody = """
            <path d='M2 12s3.8-6.5 10-6.5S22 12 22 12s-3.8 6.5-10 6.5S2 12 2 12Z'/>
            <circle cx='12' cy='12' r='3.1'/>
        """.trimIndent(),
        strokeColor = strokeColor,
    )

private fun eyeOffIcon(strokeColor: String): String =
    svgIconDataUri(
        svgBody = """
            <path d='M10.8 5.6c.4-.1.8-.1 1.2-.1 6.2 0 10 6.5 10 6.5a19.8 19.8 0 0 1-4.4 4.9'/>
            <path d='M6.2 6.2A19.4 19.4 0 0 0 2 12s3.8 6.5 10 6.5c1.8 0 3.5-.5 4.9-1.4'/>
            <path d='M10 10a3 3 0 0 0 4 4'/>
            <path d='M3 3l18 18'/>
        """.trimIndent(),
        strokeColor = strokeColor,
    )

private fun svgIconDataUri(
    svgBody: String,
    strokeColor: String,
): String {
    val encodedColor = strokeColor.removePrefix("#").let { "%23$it" }
    val svg = """
        <svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none' stroke='$encodedColor' stroke-width='1.9' stroke-linecap='round' stroke-linejoin='round'>$svgBody</svg>
    """.trimIndent()
        .replace("\n", "")
        .replace("  ", "")

    return "data:image/svg+xml;utf8,$svg"
}
